import React, { useState, useEffect } from "react";
import { DataTable, Badge, Modal, EmptyState as Empty } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Eye, Check, X, Receipt } from "lucide-react";
import API from "@/lib/API";
import toast from "react-hot-toast";
import { FilterSection } from "@/components/ui/FilterSection";

export default function ExpenseApprovalTab() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: "all", fromDate: "", toDate: "" });
  const [isExporting, setIsExporting] = useState(false);

  const handleSetFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await API.get("/general-expenses");
      const allExpenses = res.data?.data || res.data || res || [];
      // Show all expenses instead of just pending, so they don't disappear after approval
      setExpenses(allExpenses);
    } catch (err) {
      console.error("Failed to fetch pending expenses:", err);
      toast.error("Failed to fetch expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleActionClick = (expense, type) => {
    setSelectedExpense(expense);
    setActionType(type);
    if (type === "approve") {
      setApprovedAmount(Math.abs(expense.amount));
    } else {
      setApprovedAmount("");
    }
    setApprovalNote("");
    setIsModalOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedExpense) return;
    setActionLoading(true);
    try {
      let newStatus = "rejected";
      let payload = { status: "rejected", ownerMessage: approvalNote };

      if (actionType === "approve") {
        const reqAmt = Math.abs(selectedExpense.amount);
        const appAmt = Number(approvedAmount);
        
        newStatus = appAmt < reqAmt ? "partially_approved" : "approved";
        payload = { 
          status: newStatus, 
          approvedAmount: appAmt, 
          ownerMessage: approvalNote 
        };
      }

      await API.patch(`/general-expenses/${selectedExpense._id}`, payload);
      toast.success(`Expense ${newStatus} successfully`);
      setIsModalOpen(false);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (err) {
      console.error("Action failed:", err);
      toast.error(err.response?.data?.message || `Failed to ${actionType} expense`);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredExpenses = React.useMemo(() => {
    let result = [...expenses];

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(e => 
        e.expenseId?.toLowerCase().includes(lower) ||
        e.projectDetails?.customerName?.toLowerCase().includes(lower) ||
        e.projectDetails?.projectId?.toLowerCase().includes(lower) ||
        e.reason?.toLowerCase().includes(lower) ||
        e.toWhomPaid?.toLowerCase().includes(lower)
      );
    }

    const { date, fromDate, toDate } = filters;
    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((e) => {
          const createdAt = new Date(e.date || e.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [expenses, search, filters]);

  const handleExport = async () => {
    if (isExporting || expenses.length === 0) return;
    try {
      setIsExporting(true);
      const { exportToExcel } = await import('@/utils/exportUtils');
      
      const exportData = filteredExpenses.map(e => {
        const isProject = e.relatedToProject === true || String(e.relatedToProject).toLowerCase() === 'yes';
        const typeStr = isProject ? "Project" : "General";
        const customerDetails = isProject ? `${e.projectDetails?.customerName || "Unknown"} (${e.projectDetails?.projectId || "N/A"})` : "—";
        
        return {
          "ID": e.expenseId || "—",
          "Type": typeStr,
          "Customer Details": customerDetails,
          "Paid To": e.toWhomPaid || "—",
          "Reason": e.reason || "—",
          "Notes": e.notes || "—",
          "Amount": Math.abs(e.amount || 0),
          "Request Date": e.date ? new Date(e.date).toLocaleDateString('en-IN') : "—",
          "Requested By": e.byWhomPaid || "Staff",
          "Status": (e.status || "pending").replace("_", " ").toUpperCase()
        };
      });

      const pad = n => String(n).padStart(2, '0');
      const nowDt = new Date();
      const dtStr = `${nowDt.getFullYear()}-${pad(nowDt.getMonth()+1)}-${pad(nowDt.getDate())}`;

      await exportToExcel(exportData, `Expense_Approvals_Export_${dtStr}`);
      toast.success("Export completed successfully");
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Failed to export expenses");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      header: "ID",
      key: "expenseId",
      render: (e) => (
        <span className="text-[10px] font-bold text-brand-orange whitespace-nowrap">
          #{e.expenseId || "—"}
        </span>
      ),
    },
    {
      header: "Type",
      key: "type",
      render: (e) => {
        const isProject = e.relatedToProject === true || String(e.relatedToProject).toLowerCase() === 'yes';
        return (
          <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${
            isProject ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'
          }`}>
            {isProject ? "Project" : "General"}
          </span>
        );
      },
    },
    {
      header: "Customer Details",
      key: "customerDetails",
      render: (e) => {
        const isProject = e.relatedToProject === true || String(e.relatedToProject).toLowerCase() === 'yes';
        if (!isProject) {
          return <span className="text-[11px] text-slate-400 italic">—</span>;
        }
        
        const customerName = e.projectDetails?.customerName || "Unknown Customer";
        const projectId = e.projectDetails?.projectId || "N/A";
        
        return (
          <div className="flex flex-col min-w-0 py-1">
            <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate">
              {customerName}
            </span>
            <span className="text-[10px] text-brand-blue font-bold truncate mt-0.5 uppercase tracking-wider">
              {projectId}
            </span>
          </div>
        );
      },
    },
    {
      header: "Paid To",
      key: "toWhomPaid",
      render: (e) => (
        <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate capitalize">
          {e.toWhomPaid || "Unknown"}
        </span>
      ),
    },
    {
      header: "Reason",
      key: "reason",
      render: (e) => (
        <span className="text-[12px] font-medium text-slate-700 capitalize whitespace-nowrap block" title={e.reason}>
          {e.reason || "Expense Statement"}
        </span>
      ),
    },
    {
      header: "Notes",
      key: "notes",
      render: (e) => {
        if (!e.notes) return <span className="text-[11px] text-slate-400 italic">—</span>;
        return (
          <span className="text-[11px] text-slate-500 whitespace-nowrap italic block" title={e.notes}>
            {e.notes}
          </span>
        );
      },
    },
    {
      header: "Amount",
      key: "amount",
      render: (e) => (
        <span className="text-sm font-black text-rose-500">
          {fmt(Math.abs(e.amount))}
        </span>
      ),
    },
    {
      header: "Request Date",
      key: "date",
      render: (e) => (
        <span className="text-[11px] font-medium text-slate-600">
          {fmtDate(e.date)}
        </span>
      ),
    },
    {
      header: "Requested By",
      key: "requestedBy",
      render: (e) => (
        <span className="text-[11px] font-bold text-slate-600 uppercase whitespace-nowrap">
          {e.byWhomPaid || "Staff"}
        </span>
      ),
    },
    {
      header: "Status",
      key: "status",
      render: (e) => {
        const s = e.status || "pending";
        const colors = {
          pending: "bg-amber-100 text-amber-700 border-amber-200",
          approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
          partially_approved: "bg-blue-100 text-blue-700 border-blue-200",
          rejected: "bg-red-100 text-red-700 border-red-200",
        };
        return (
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider border whitespace-nowrap ${colors[s] || colors.pending}`}>
            {s.replace("_", " ")}
          </span>
        );
      },
    },
    {
      header: "Actions",
      key: "actions",
      render: (e) => {
        if (e.status && e.status !== "pending") {
          return <span className="text-[11px] text-slate-400 italic whitespace-nowrap">Processed</span>;
        }
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleActionClick(e, "approve")}
              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
              title="Approve Expense"
            >
              <Check size={18} strokeWidth={2.5} />
            </button>
            <button
              onClick={() => handleActionClick(e, "reject")}
              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Reject Expense"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={handleSetFilters}
          onExport={handleExport}
          isExporting={isExporting}
        />
      </div>

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        {filteredExpenses.length === 0 && !loading ? (
          <div className="py-12">
            <Empty icon="🧾" title="No Pending Expenses" desc="All expenses have been processed." />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredExpenses}
            isLoading={loading}
            className="border-none shadow-none rounded-none"
          />
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={actionType === 'approve' ? 'Approve expense request' : 'Reject expense request'}
        size="max-w-md"
      >
        <div className="space-y-4">
          {actionType === 'approve' ? (
            <>
              <p className="text-[13px] text-slate-500 font-medium bg-slate-50 p-3 rounded-lg border border-slate-100 leading-relaxed">
                If approved amount is below the requested total (even by ₹1), status becomes <span className="font-bold text-slate-700">partial approved</span>. Equal amount means <span className="font-bold text-slate-700">full approved</span>.
              </p>

              <div className="flex flex-col gap-3 p-4 bg-slate-50/50 rounded-xl border border-slate-100">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                  <span className="text-[11px] font-bold text-slate-600 capitalize">Expense id</span>
                  <span className="text-[11px] font-bold text-brand-orange uppercase">{selectedExpense?.expenseId}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Requested</span>
                  <span className="text-[13px] font-bold text-slate-800">₹{fmt(Math.abs(selectedExpense?.amount || 0))}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-[13px] font-medium text-slate-500">Reason</span>
                  <span className="text-[13px] font-medium text-slate-800 max-w-[200px] text-right truncate" title={selectedExpense?.reason}>
                    {selectedExpense?.reason || "—"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">
                    Approved amount (₹) <span className="text-rose-500">*</span>
                  </label>
                  <input 
                    type="number"
                    className="w-full h-11 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-colors font-medium text-slate-800"
                    value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">
                    Approval Note (optional)
                  </label>
                  <textarea 
                    className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue resize-none h-24 transition-colors"
                    placeholder="Add internal remarks or reason for partial approval..."
                    value={approvalNote}
                    onChange={(e) => setApprovalNote(e.target.value)}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Are you sure you want to reject the expense request for 
                <span className="font-bold text-brand-dark mx-1">
                  ₹{selectedExpense?.amount ? fmt(Math.abs(selectedExpense.amount)) : 0}
                </span> 
                by {selectedExpense?.byWhomPaid || "Staff"}?
              </p>
              <div>
                <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">
                  Rejection Note (optional)
                </label>
                <textarea 
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue resize-none h-24 transition-colors"
                  placeholder="Add reason for rejection..."
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="btn-secondary flex-1"
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              onClick={confirmAction}
              className={`btn-primary flex-1 flex justify-center items-center ${
                actionType === "reject" ? "bg-red-600 hover:bg-red-700 border-red-600" : "bg-brand-orange hover:bg-orange-600 border-brand-orange"
              }`}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                actionType === "approve" ? "Confirm Approve" : "Confirm Reject"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
